<script setup lang="ts">
import type { UnwrapRefCarouselApi } from "../ui/carousel/interface";

const reels = [
  {
    src: "/videos/social/reel-van-gogh.mp4",
    titleKey: "reels.items.van-gogh.title",
    descKey: "reels.items.van-gogh.description",
  },
  {
    src: "/videos/social/reel-neuronio.mp4",
    titleKey: "reels.items.neuronio.title",
    descKey: "reels.items.neuronio.description",
  },
  {
    src: "/videos/social/reel-preview.mp4",
    titleKey: "reels.items.preview.title",
    descKey: "reels.items.preview.description",
  },
  {
    src: "/videos/social/reel-good-charlotte.mp4",
    titleKey: "reels.items.good-charlotte.title",
    descKey: "reels.items.good-charlotte.description",
  },
  {
    src: "/videos/social/reel-taste-of-ink.mp4",
    titleKey: "reels.items.taste-of-ink.title",
    descKey: "reels.items.taste-of-ink.description",
  },
  {
    src: "/videos/social/reel-sweetness.mp4",
    titleKey: "reels.items.sweetness.title",
    descKey: "reels.items.sweetness.description",
  },
];

const carouselApi = ref<UnwrapRefCarouselApi>();
const activeIndex = ref(0);
const playingIndex = ref<number | null>(null);

function normalizeIndex(index: number) {
  return ((index % reels.length) + reels.length) % reels.length;
}

function getVideoForIndex(index: number): HTMLVideoElement | null {
  const api = carouselApi.value;
  if (!api) return null;

  const slide = api.slideNodes()[index];
  return slide?.querySelector("video") ?? null;
}

function pauseAllVideos(exceptIndex?: number) {
  const api = carouselApi.value;
  if (!api) return;

  api.slideNodes().forEach((slide, index) => {
    if (index === exceptIndex) return;
    const video = slide.querySelector("video");
    if (!video) return;
    video.pause();
    video.currentTime = 0;
    video.muted = true;
  });

  if (exceptIndex === undefined || playingIndex.value !== exceptIndex) {
    playingIndex.value = null;
  }
}

function onSlideChange() {
  const api = carouselApi.value;
  if (!api) return;

  activeIndex.value = normalizeIndex(api.selectedScrollSnap());
  pauseAllVideos();
}

function scrollToReel(index: number) {
  carouselApi.value?.scrollTo(index);
}

function togglePlayback(index: number, event: Event) {
  event.stopPropagation();

  if (index !== activeIndex.value) {
    scrollToReel(index);
  }

  const video = getVideoForIndex(index);
  if (!video) return;

  if (playingIndex.value === index) {
    video.pause();
    playingIndex.value = null;
    return;
  }

  pauseAllVideos(index);
  video.muted = false;
  video.play().then(() => {
    playingIndex.value = index;
  }).catch(() => {});
}

function onCarouselInit(api: UnwrapRefCarouselApi) {
  if (!api) return;

  carouselApi.value = api;
  api.on("select", onSlideChange);
  api.on("reInit", onSlideChange);
  nextTick(onSlideChange);
}
</script>

<template>
  <UtilsSection
    class="bg-[#070d18] text-white overflow-hidden"
    container-class="max-w-screen-xl"
  >
    <div class="text-center mb-12">
      <p
        class="text-[#27C9F4] uppercase tracking-[0.3em] text-xs font-medium mb-4"
        data-aos="fade-up"
      >
        {{ $t("reels.eyebrow") }}
      </p>
      <UtilsTitle as="h2" class="text-white text-4xl md:text-5xl" data-aos="fade-up">
        {{ $t("reels.title") }}
      </UtilsTitle>
      <UtilsSubtitle class="mt-4 text-white/60 text-lg" data-aos="fade-up">
        {{ $t("reels.subtitle") }}
      </UtilsSubtitle>
    </div>

    <ClientOnly>
      <Carousel
        class="w-full px-10 sm:px-12"
        :opts="{ loop: false, align: 'start', containScroll: 'trimSnaps' }"
        @init-api="onCarouselInit"
      >
        <CarouselContent class="-ml-4">
          <CarouselItem
            v-for="(reel, index) in reels"
            :key="reel.src"
            class="pl-4 basis-[85%] sm:basis-1/2 md:basis-1/3"
          >
            <div
              class="group relative w-full rounded-2xl overflow-hidden shadow-2xl bg-black text-left transition-shadow duration-300"
              :class="
                playingIndex === index
                  ? 'ring-2 ring-[#27C9F4] shadow-[0_0_30px_rgba(39,201,244,0.25)]'
                  : 'ring-1 ring-white/10 hover:ring-[#27C9F4]/50'
              "
            >
              <button
                type="button"
                class="absolute inset-0 z-20 flex items-center justify-center"
                :aria-label="
                  playingIndex === index ? $t('reels.pause') : $t('reels.play')
                "
                @click="togglePlayback(index, $event)"
              >
                <span
                  class="flex h-14 w-14 items-center justify-center rounded-full shadow-lg transition-all duration-300"
                  :class="
                    playingIndex === index
                      ? 'bg-black/55 text-white opacity-0 scale-95 group-hover:opacity-100 group-hover:scale-100'
                      : 'bg-white/95 text-[#070d18] opacity-90 scale-100 group-hover:opacity-100 group-hover:scale-105'
                  "
                >
                  <Icon
                    :name="playingIndex === index ? 'ic:round-pause' : 'ic:round-play-arrow'"
                    class="text-3xl"
                    :class="playingIndex === index ? '' : 'ml-0.5'"
                  />
                </span>
              </button>

              <div class="aspect-[9/16] overflow-hidden">
                <video
                  :src="reel.src"
                  class="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-110"
                  muted
                  loop
                  playsinline
                  preload="metadata"
                />
              </div>

              <div
                class="absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-5 pt-16 pointer-events-none"
              >
                <h3 class="font-neutron italic text-xl text-[#FEE000]">
                  {{ $t(reel.titleKey) }}
                </h3>
                <p class="text-sm text-white/70 mt-1 leading-snug">
                  {{ $t(reel.descKey) }}
                </p>
              </div>
            </div>
          </CarouselItem>
        </CarouselContent>
        <CarouselPrevious
          class="flex border-white/20 bg-white/10 text-white hover:bg-white/20 left-0 sm:-left-2"
        />
        <CarouselNext
          class="flex border-white/20 bg-white/10 text-white hover:bg-white/20 right-0 sm:-right-2"
        />
      </Carousel>
    </ClientOnly>
  </UtilsSection>
</template>
